import React, { useState, useEffect } from 'react';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { SuperAdminService } from '@/services/superadmin.service';
import { toast, Toaster } from 'react-hot-toast';
import { Megaphone, Plus, Check, X, AlertTriangle, Rocket, Wrench, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '@/assets/Sthitih_Logo.png';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'maintenance' | 'release' | 'emergency' | 'general';
  target_clients: string;
  created_at: string;
}

const typeIcons: Record<string, React.ReactNode> = {
  maintenance: <Wrench className="w-5 h-5 text-[#8A4B22]" />,
  release: <Rocket className="w-5 h-5 text-[#1F5A43]" />,
  emergency: <AlertTriangle className="w-5 h-5 text-[#D64545]" />,
  general: <Bell className="w-5 h-5 text-[#6F706C]" />,
};

const typeColors: Record<string, string> = {
  maintenance: 'bg-[#EFE5D6] border-[#DDD0BE] text-[#8A4B22]',
  release: 'bg-[#DFF5E7] border-[#1F7A4F]/20 text-[#1F7A4F]',
  emergency: 'bg-[#FCE8E8] border-[#D64545]/20 text-[#D64545]',
  general: 'bg-[#E7F4F0] border-[#2E7D68]/20 text-[#2E7D68]',
};

const Announcements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resultModal, setResultModal] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [form, setForm] = useState({ title: '', content: '', type: 'general' as Announcement['type'], target_clients: 'all' });

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await SuperAdminService.getAnnouncements();
      setAnnouncements(data);
    } catch {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleCreate = async () => {
    if (!form.title || !form.content) {
      toast.error('Title and content are required');
      return;
    }
    try {
      await SuperAdminService.createAnnouncement(form);
      setResultModal({ type: 'success', message: 'Announcement published successfully' });
      setShowCreate(false);
      setForm({ title: '', content: '', type: 'general', target_clients: 'all' });
      fetchAnnouncements();
    } catch {
      setResultModal({ type: 'error', message: 'Failed to publish announcement' });
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F7F2EA] text-[#243238]">
      <Toaster position="top-right" />
      <SuperAdminSidebar />

      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title="Announcements" />

        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] shadow-xs items-center justify-between sticky top-0 z-20 w-full">
          <h1 className="text-sm sm:text-base font-extrabold tracking-tight text-[#243238] uppercase">Platform Announcements</h1>
          <img src={logoImg} alt="Sthitih Logo" className="h-10 w-auto object-contain rounded-md shadow-xs border border-black/5" />
        </header>

        <main className="flex-grow p-4 sm:p-8">
          <div className="flex justify-end mb-6">
            <button onClick={() => setShowCreate(true)} className="btn-primary text-xs flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Announcement
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-[#1F5A43] border-t-transparent rounded-full animate-spin" /></div>
          ) : announcements.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#8D8A83]">
              <Megaphone className="w-12 h-12 mb-3 opacity-40 text-[#1F5A43]" />
              <p className="text-lg font-medium text-[#6F706C]">No announcements yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((a) => (
                <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`rounded-[18px] border p-6 shadow-earth ${typeColors[a.type]}`}>
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5">{typeIcons[a.type]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-base text-[#243238]">{a.title}</h3>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#FFFDF9] border border-[#DDD0BE]">{a.type}</span>
                      </div>
                      <p className="text-sm opacity-90 leading-relaxed mb-3 text-[#243238]">{a.content}</p>
                      <div className="flex items-center gap-4 text-xs opacity-70">
                        <span>Target: {a.target_clients === 'all' ? 'All Clients' : 'Selected'}</span>
                        <span>•</span>
                        <span>{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]" onClick={() => setShowCreate(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFDF9] rounded-[18px] border border-[#E4D8C8] shadow-earth p-8 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
              <h2 className="text-sm font-bold text-[#243238] uppercase tracking-wider mb-6">Publish Announcement</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#4A5558] mb-1.5">Title</label>
                  <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Announcement title"
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none text-xs text-[#243238] bg-white font-semibold" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#4A5558] mb-1.5">Content</label>
                  <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} placeholder="Write the announcement message..."
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none resize-none text-xs text-[#243238] bg-white font-semibold" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#4A5558] mb-1.5">Type</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as any })}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none bg-white text-xs text-[#243238] font-semibold">
                    <option value="general">General</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="release">Release Notes</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#4A5558] mb-1.5">Target</label>
                  <select value={form.target_clients} onChange={e => setForm({ ...form, target_clients: e.target.value })}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none bg-white text-xs text-[#243238] font-semibold">
                    <option value="all">All Clients</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={() => setShowCreate(false)} className="flex-1 btn-secondary text-xs">Cancel</button>
                <button onClick={handleCreate} className="flex-1 btn-primary text-xs">Publish</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result Modal */}
      <AnimatePresence>
        {resultModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1001]" onClick={() => setResultModal(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFDF9] rounded-[18px] border border-[#E4D8C8] shadow-earth p-8 max-w-sm w-full mx-4 text-center" onClick={e => e.stopPropagation()}>
              <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${resultModal.type === 'success' ? 'bg-[#DFF5E7] text-[#1F7A4F]' : 'bg-[#FCE8E8] text-[#D64545]'}`}>
                {resultModal.type === 'success' ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
              </div>
              <h3 className="text-sm font-bold text-[#243238] uppercase tracking-wider mb-2">{resultModal.type === 'success' ? 'Success' : 'Error'}</h3>
              <p className="text-xs text-[#6F706C] mb-6">{resultModal.message}</p>
              <button onClick={() => setResultModal(null)} className="btn-primary text-xs px-6 py-2.5">Close</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Announcements;
